import {parseResponse} from "../api"

export default class InvoiceService {
  constructor(axios) {
    // todo(egor): add config for domain
    this.axios = axios
  }

  async getFilteredInvoices({clientIds, statuses, pagination}) {
    return this.axios.post('/api/invoices-filtered', {
      clientIds: clientIds,
      statuses: statuses,
      pagination: pagination
    }).then((response) => {
      return parseResponse(response)
    })
  }

  async getInvoice({invoiceId}) {
    if (!invoiceId) {
      throw Error("Can't be empty `invoiceId`")
    }

    if (!_.isNumber(invoiceId)) {
      throw Error("`invoiceId` must be number")
    }

    return this.axios.post('/api/invoices-get', {
      invoiceId: invoiceId,
    }).then((response) => {
      return parseResponse(response)
    })
  }

  async createInvoice({clientId, costCenterId, regionId, from, to}) {
    const createWithCostCenter = costCenterId && regionId
    if (clientId && createWithCostCenter) {
      throw Error("Can't create both invoices with clientId && (costCenterId && regionId)")
    }
    
    if (!clientId && !createWithCostCenter) {
      throw Error("Can't create noone invoices")
    }

    if (!clientId && !costCenterId) {
      throw Error("Can't create invoice with empty costCenterId or clientId")
    }

    if (!clientId && !regionId) {
      throw Error("Can't create invoice with empty regionId or clientId")
    }
    
    if (!from) {
      throw Error("Can't create invoice with empty `to`")
    }

    if (!to) {
      throw Error("Can't create invoice with empty `to`")
    }

    return this.axios.post(`/api/invoices-create`, {
      costCenterId,
      regionId,
      clientId,
      from,
      to
    })
      .then((response) => {
        return parseResponse(response)
      })
  }

  async exportCostCenterInvoiceToExcel({regionalInvoiceId, shortVersion = true}) {
    if (!regionalInvoiceId) {
      throw Error("Can't export invoice with empty `regionalInvoiceId`")
    }

    return this.axios.post(`/api/invoices-export-regional-invoices-to-excel`, {regionalInvoiceId, shortVersion}, {
      responseType: 'arraybuffer',
    })
  }

  async updateInvoice({invoiceId, from, to, ...others}) {
    if (!invoiceId) {
      throw Error("Can't update invoice with empty invoiceId")
    }

    if (!from) {
      throw Error("Can't update invoice with empty `to`")
    }

    if (!to) {
      throw Error("Can't update invoice with empty `to`")
    }

    return this.axios.post(`/api/invoices-update`, {
      id: invoiceId,
      from: from,
      to: to,
      ...others
    })
      .then((response) => {
        return parseResponse(response)
      })
  }

  async deleteInvoice({invoiceId}) {
    if (!invoiceId) {
      throw Error("Can't be null empty invoiceId")
    }

    return this.axios.post(`/api/invoices-delete`, {
      invoiceId: invoiceId,
    })
      .then((response) => {
        return parseResponse(response)
      })
  }

  async attachCasesWithoutAnomaliesToInvoice({invoiceId}) {
    if (!invoiceId) {
      throw Error("`invoiceId` can't be null")
    }

    return this.axios.post(`/api/invoices-attach-cases-without-anomalies-to-invoice`, {
      invoiceId: invoiceId,
    })
      .then((response) => {
        return parseResponse(response)
      })
  }

  async attachLogsToInvoice({invoiceId, logIds}) {
    if (!invoiceId) {
      throw Error("`invoiceId` can't be null")
    }
    if (!logIds) {
      throw Error("`logIds` can't be null")
    }

    return this.axios.post(`/api/invoices-attach-logs-to-invoice`, {
      invoiceId: invoiceId,
      logIds: logIds
    })
      .then((response) => {
        return parseResponse(response)
      })
  }

  async changeInvoiceStatus({invoiceId, newStatus, comment}) {
    if (!invoiceId) {
      throw Error("`invoiceId` can't be null")
    }
    if (!newStatus) {
      throw Error("`newStatus` can't be null")
    }

    return this.axios.post(`/api/invoices-change-invoice-status`, {
      invoiceId: invoiceId,
      newStatus: newStatus,
      comment: comment
    })
      .then((response) => {
        return parseResponse(response)
      })
  }

  async detachLogsToInvoice({invoiceId, logIds}) {
    if (!invoiceId) {
      throw Error("`invoiceId` can't be null")
    }
    if (!logIds) {
      throw Error("`logIds` can't be null")
    }

    return this.axios.post(`/api/invoices-detach-logs-to-invoice`, {
      invoiceId: invoiceId,
      logIds: logIds
    })
      .then((response) => {
        return parseResponse(response)
      })
  }

  async getInvoicesLogCandidates({invoiceId, pagination, metaFilters, logMode, caseId, title, dateRange, caseAnomalyFilter}) {
    if (!invoiceId) {
      throw Error("`invoiceId` can't be null")
    }

    let request = {
      invoiceId: invoiceId,
      filter: {
        metaFilters: metaFilters,
        logMode: logMode,
        caseId: caseId,
        dateRange: dateRange,
        title: title,
        caseAnomalyFilter: caseAnomalyFilter,
      },
      pagination: pagination,
    };

    return this.axios.post(`/api/invoices-get-invoice-log-candidates`, request)
      .then((response) => {
        return parseResponse(response)
      })
  }

  async calculateInvoiceAdditionalInfo({invoiceId}) {
    if (!invoiceId) {
      throw Error("`invoiceId` can't be null")
    }

    let request = {
      invoiceId: invoiceId,
    };

    return this.axios.post(`/api/invoices-calculate-invoice-additional-info`, request)
      .then((response) => {
        return parseResponse(response)
      })
  }

  async exportInvoiceToCsv({invoiceId}) {
    if (!invoiceId) {
      throw Error("`invoiceId` can't be null")
    }

    let request = {
      invoiceId: invoiceId
    };

    return this.axios.post(`/api/invoices-export-invoice-to-csv`, request)
  }

  async exportInvoiceToExcel({invoiceId, shortVersion = false}) {
    if (!invoiceId) {
      throw Error("`invoiceId` can't be null")
    }

    let request = {
      invoiceId: invoiceId,
      shortVersion: shortVersion
    };

    return this.axios.post(`/api/invoices-export-invoice-to-excel`, request, {
      responseType: 'arraybuffer',
    })
  }

  async sendInvoiceToClient({invoiceId}) {
    if (!invoiceId) {
      throw Error("`invoiceId` can't be null")
    }

    let request = {
      invoiceId: invoiceId,
    };

    return this.axios.post(`/api/invoices-send-invoice-to-client`, request)
      .then((response) => {
        return parseResponse(response)
      })
  }
}




// WEBPACK FOOTER //
// ./src/services/InvoiceService.js